import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
    PieChart, Pie, Cell, Label,
    Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import Navbar from './Navbar';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from 'date-fns/locale/th';
import { Calendar, Search, FileText, Clock, ChevronDown, ChevronUp, Check, Tag, Car, ChevronsUpDown } from 'lucide-react';
import { API_URL } from '../config';

registerLocale('th', th);

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด", icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="w-full md:w-auto min-w-[140px] relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left ${isOpen ? '' : ''
                    }`}
            >
                {Icon && (
                    <div className={`p-1 rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-blue-100' : 'bg-blue-50'}`}>
                        <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                )}
                <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-1">{label}</span>
                    <span className="text-xs font-bold text-slate-800">
                        {selectedOption?.label || allLabel}
                    </span>
                </div>
                <div className={`text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown className="h-3 w-3" />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-[60] mt-1.5 min-w-full w-max max-w-[240px] left-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                    <button
                        onClick={() => { onChange(''); setIsOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${(!value || value === 'All') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <span>{allLabel}</span>
                        {(!value || value === 'All') && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    <div className="max-h-[200px] overflow-y-auto scrollbar-hide">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {String(value) === String(opt.value) && <Check className="w-3.5 h-3.5" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const ReportManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({
        status_counts: [],
        total_slots: 0,
        unique_slots_audited: 0
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [rounds, setRounds] = useState([]);
    const [selectedRound, setSelectedRound] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [consecutiveLogs, setConsecutiveLogs] = useState([]);
    const [, setLoadingConsecutive] = useState(false);
    const [selectedPlate, setSelectedPlate] = useState(null);
    const [plateHistory, setPlateHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const reportRef = useRef(null);
    const isInitialLoadDone = useRef(false);

    // Helper: compute default round from rounds list
    const computeDefaultRound = (roundsList) => {
        if (!roundsList || roundsList.length === 0) return '';
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        let defaultRound = null;
        let maxMinutes = -1;
        roundsList.forEach(r => {
            const [hours, minutes] = r.scheduled_time.split(':').map(Number);
            const roundMinutes = hours * 60 + minutes;
            if (currentMinutes >= roundMinutes && roundMinutes > maxMinutes) {
                maxMinutes = roundMinutes;
                defaultRound = r;
            }
        });
        return defaultRound ? defaultRound.id : '';
    };

    const fetchDataWithParams = useCallback(async ({ roundId, dateObj, type, status, search }) => {
        setLoading(true);
        try {
            const dateStr = dateObj.toLocaleDateString('en-CA');
            const [summaryRes, logsRes] = await Promise.all([
                axios.get(`${API_URL}/api/reports/summary`, {
                    params: {
                        startDate: dateStr,
                        endDate: dateStr,
                        round_id: roundId,
                        vehicle_type: type,
                        status: status,
                    },
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/api/reports/logs`, {
                    params: {
                        startDate: dateStr,
                        endDate: dateStr,
                        searchTerm: search,
                        round_id: roundId,
                        vehicle_type: type,
                        status: status
                    },
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setSummary(summaryRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error('Failed to fetch report data', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchConsecutiveParkingWithParams = useCallback(async ({ dateObj, type }) => {
        setLoadingConsecutive(true);
        try {
            const dateStr = dateObj.toLocaleDateString('en-CA');
            const res = await axios.get(`${API_URL}/api/reports/consecutive-parking`, {
                params: { date: dateStr, vehicle_type: type },
                headers: { Authorization: `Bearer ${token}` }
            });
            setConsecutiveLogs(res.data);
        } catch (err) {
            console.error('Failed to fetch consecutive parking data', err);
        } finally {
            setLoadingConsecutive(false);
        }
    }, [token]);

    const fetchVehicleHistory = useCallback(async (plate) => {
        setSelectedPlate(plate);
        setLoadingHistory(true);
        setIsHistoryModalOpen(true);
        try {
            const res = await axios.get(`${API_URL}/api/reports/vehicle-history?license_plate=${encodeURIComponent(plate)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPlateHistory(res.data);
        } catch (err) {
            console.error('Failed to fetch vehicle history', err);
        } finally {
            setLoadingHistory(false);
        }
    }, [token]);

    // Initial load: fetch rounds first, then fetch data once with correct round
    useEffect(() => {
        isInitialLoadDone.current = false;
        let cancelled = false;
        const init = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/rounds?active=true`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (cancelled) return;
                const roundsList = res.data;
                setRounds(roundsList);
                const defaultRoundId = computeDefaultRound(roundsList);
                setSelectedRound(defaultRoundId);
                // Fetch data once with the correct round
                await Promise.all([
                    fetchDataWithParams({
                        roundId: defaultRoundId,
                        dateObj: new Date(),
                        type: '',
                        status: '',
                        search: ''
                    }),
                    fetchConsecutiveParkingWithParams({
                        dateObj: new Date(),
                        type: ''
                    })
                ]);
                if (!cancelled) isInitialLoadDone.current = true;
            } catch (err) {
                console.error('Initial load failed', err);
                if (!cancelled) setLoading(false);
            }
        };
        init();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Re-fetch when filters change (skip on initial load)
    const fetchData = useCallback(async () => {
        if (!isInitialLoadDone.current) return;
        await fetchDataWithParams({
            roundId: selectedRound,
            dateObj: selectedDate,
            type: selectedType,
            status: selectedStatus,
            search: searchTerm
        });
    }, [fetchDataWithParams, selectedRound, selectedDate, selectedType, selectedStatus, searchTerm]);

    const fetchConsecutiveParking = useCallback(async () => {
        if (!isInitialLoadDone.current) return;
        await fetchConsecutiveParkingWithParams({
            dateObj: selectedDate,
            type: selectedType
        });
    }, [fetchConsecutiveParkingWithParams, selectedDate, selectedType]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 50);
        return () => clearTimeout(timer);
    }, [fetchData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchConsecutiveParking();
        }, 50);
        return () => clearTimeout(timer);
    }, [fetchConsecutiveParking]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedRound, selectedType, selectedStatus, selectedDate, rowsPerPage]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        } else if (sortConfig.key !== key) {
            // Default to 'desc' for date/time columns when clicked for the first time
            if (key === 'created_at' || key === 'log_date' || key === 'round_time') {
                direction = 'desc';
            }
        }
        setSortConfig({ key, direction });
    };

    const sortedLogs = useMemo(() => {
        let sortableItems = [...logs];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle null/undefined
                if (aValue === null || aValue === undefined) aValue = '';
                if (bValue === null || bValue === undefined) bValue = '';

                // Handle numbers
                if (!isNaN(aValue) && !isNaN(bValue) && aValue !== '' && bValue !== '') {
                    aValue = Number(aValue);
                    bValue = Number(bValue);
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [logs, sortConfig]);

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ChevronsUpDown className="w-2.5 h-2.5 text-slate-300" />;
        return sortConfig.direction === 'asc' ?
            <ChevronUp className="w-2.5 h-2.5 text-blue-500" /> :
            <ChevronDown className="w-2.5 h-2.5 text-blue-500" />;
    };

    const totalItems = logs.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentLogs = sortedLogs.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const getStatusCounts = () => {
        const counts = {
            Total: (summary.status_counts || []).reduce((acc, curr) => acc + Number(curr.count || 0), 0),
            Normal: (summary.status_counts || []).find(s => s.status?.toLowerCase() === 'normal')?.count || 0,
            Unclear: (summary.status_counts || []).find(s => s.status?.toLowerCase() === 'unclear')?.count || 0,
            Empty: (summary.status_counts || []).find(s => s.status?.toLowerCase() === 'empty')?.count || 0,
            'No-Sticker': (summary.status_counts || []).find(s => s.status?.toLowerCase() === 'no-sticker')?.count || 0,
            Visitor: (summary.status_counts || []).find(s => s.status?.toLowerCase() === 'visitor')?.count || 0,
        };
        return counts;
    };

    const counts = getStatusCounts();

    // Filter future rounds if viewing today's report
    const filteredRounds = useMemo(() => {
        const todayStr = new Date().toLocaleDateString('en-CA');
        const selectedStr = selectedDate.toLocaleDateString('en-CA');

        // Only filter if looking at today
        if (selectedStr === todayStr) {
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            return rounds.filter(r => {
                const [hours, minutes] = r.scheduled_time.split(':').map(Number);
                const roundMinutes = hours * 60 + minutes;
                return currentMinutes >= roundMinutes;
            });
        }
        return rounds;
    }, [rounds, selectedDate]);
    const successRate = counts.Total > 0 ? Math.round(((counts.Normal + counts.Visitor + counts.Empty) / counts.Total) * 100) : 0;

    // --- Chart Data Calculations ---
    const pieData = useMemo(() => {
        const data = [
            { name: 'มีสติ๊กเกอร์', value: counts.Normal, color: '#10B981' },
            { name: 'Visitor', value: counts.Visitor, color: '#8B5CF6' },
            { name: 'ไม่มีสติ๊กเกอร์', value: counts['No-Sticker'], color: '#EF4444' },
            { name: 'สติ๊กเกอร์ไม่ชัดเจน', value: counts.Unclear, color: '#F59E0B' },
            { name: 'ช่องว่างไม่มีรถจอด', value: counts.Empty, color: '#64748B' },
        ];

        const auditedCount = counts.Total;
        const totalSlots = summary.total_slots || 0;
        const notAudited = totalSlots > auditedCount ? totalSlots - auditedCount : 0;

        if (notAudited > 0) {
            // If filtering by status or search, the remainder isn't just "not audited", it's "others + not audited"
            const label = (selectedStatus || searchTerm) ? "อื่นๆ / ยังไม่ได้ตรวจ" : "ยังไม่ได้ตรวจ";
            data.push({ name: label, value: notAudited, color: '#E2E8F0' });
        }

        return data.filter(item => item.value > 0);
    }, [counts, summary.total_slots, selectedStatus, searchTerm]);

    const barData = useMemo(() => {
        return (summary.zone_issues || []).map(item => {
            const noSticker = Number(item['ไม่มีสติ๊กเกอร์'] || 0);
            const unclear = Number(item['ไม่ชัดเจน'] || 0);
            const overstay = Number(item['รถจอดแช่'] || 0);
            const handbrake = Number(item['ไม่ปลดเบรกมือ'] || 0);
            return {
                ...item,
                'ไม่มีสติ๊กเกอร์': noSticker,
                'ไม่ชัดเจน': unclear,
                'รถจอดแช่': overstay,
                'ไม่ปลดเบรกมือ': handbrake,
                totalIssues: noSticker + unclear + overstay + handbrake
            };
        });
    }, [summary.zone_issues]);

    const historyWithDayNumbers = useMemo(() => {
        if (!plateHistory || plateHistory.length === 0) return [];

        // 1. Get unique dates and sort them ASCENDING to assign Day 1, Day 2...
        const uniqueDates = [...new Set(plateHistory.map(item => {
            const dateObj = new Date(item.log_date);
            return dateObj.toLocaleDateString('en-CA');
        }))].sort((a, b) => new Date(a) - new Date(b));

        // Create a mapping of date string to day number
        const dateToDayNum = {};
        uniqueDates.forEach((dateStr, index) => {
            dateToDayNum[dateStr] = index + 1;
        });

        // 2. Return the plate history sorted DESCENDING, with the day number attached
        const sortedHistory = [...plateHistory]
            .sort((a, b) => {
                // Sort by date DESC, then by round_time DESC, then by id DESC
                const dateDiff = new Date(b.log_date) - new Date(a.log_date);
                if (dateDiff !== 0) return dateDiff;

                if (b.round_time && a.round_time) {
                    if (b.round_time > a.round_time) return 1;
                    if (b.round_time < a.round_time) return -1;
                }

                return (b.id || 0) - (a.id || 0);
            });

        let lastDateStr = null;
        return sortedHistory.map(item => {
            const dateStr = new Date(item.log_date).toLocaleDateString('en-CA');
            const isFirstInDay = dateStr !== lastDateStr;
            lastDateStr = dateStr;

            return {
                ...item,
                dayNumber: dateToDayNum[dateStr],
                isFirstInDay
            };
        });
    }, [plateHistory]);

    // Remove toggleDateExpansion as it's no longer needed
    // -------------------------------

    const exportToCSV = () => {
        try {
            if (!logs || logs.length === 0) {
                alert('ไม่มีข้อมูลสำหรับการ Export');
                return;
            }

            const headers = ["วันที่", "รอบ", "โซน", "ช่องจอด", "ทะเบียน", "สติ๊กเกอร์", "ประเภท", "สถานะ", "ปลดเบรกมือ", "หมายเหตุ", "ผู้ตรวจ", "เวลาตรวจ"];

            const formatCSVField = (field) => {
                if (field === null || field === undefined) return '""';
                const stringField = String(field);
                return `"${stringField.replace(/"/g, '""')}"`;
            };

            const rows = logs.map(log => {
                let formattedDate = '-';
                if (log.log_date) {
                    const logDate = new Date(log.log_date);
                    if (!isNaN(logDate.getTime())) {
                        formattedDate = `${String(logDate.getDate()).padStart(2, '0')}/${String(logDate.getMonth() + 1).padStart(2, '0')}/${logDate.getFullYear()}`;
                    }
                }

                let formattedTime = '-';
                if (log.created_at) {
                    const createdAt = new Date(log.created_at);
                    if (!isNaN(createdAt.getTime())) {
                        formattedTime = `${String(createdAt.getDate()).padStart(2, '0')}/${String(createdAt.getMonth() + 1).padStart(2, '0')}/${createdAt.getFullYear()} ${String(createdAt.getHours()).padStart(2, '0')}:${String(createdAt.getMinutes()).padStart(2, '0')}:${String(createdAt.getSeconds()).padStart(2, '0')}`;
                    }
                }

                return [
                    formatCSVField(formattedDate),
                    formatCSVField(log.round_time?.substring(0, 5) + ' น.' || '-'),
                    formatCSVField(log.zone || '-'),
                    formatCSVField(log.slot_name || '-'),
                    formatCSVField(log.license_plate || '-'),
                    log.sticker_no ? `=" ${log.sticker_no} "` : '"-"',
                    formatCSVField(log.status === 'Empty' ? '-' : (log.vehicle_type === 'Car' ? 'รถยนต์' : 'มอเตอร์ไซค์')),
                    formatCSVField(log.status === 'Empty' ? 'ไม่มีรถจอด' :
                        log.status === 'Normal' ? 'มีสติ๊กเกอร์' :
                            log.status === 'Unclear' ? 'สติ๊กเกอร์ไม่ชัดเจน' :
                                log.status === 'No-Sticker' ? 'ไม่มีสติ๊กเกอร์' :
                                    log.status),
                    formatCSVField(log.is_handbrake === 1 ? 'ปลดแล้ว' : log.is_handbrake === 0 ? 'ยังไม่ปลด' : '-'),
                    formatCSVField(log.remark || '-'),
                    formatCSVField(log.checker_name || '-'),
                    formatCSVField(formattedTime)
                ];
            });

            const formatDatePart = (date) => {
                if (!(date instanceof Date) || isNaN(date.getTime())) return { d: '00', m: '00', y: '0000' };
                return {
                    d: String(date.getDate()).padStart(2, '0'),
                    m: String(date.getMonth() + 1).padStart(2, '0'),
                    y: date.getFullYear()
                };
            };

            const d = formatDatePart(selectedDate);
            const filename = `Report_${d.d}-${d.m}-${d.y}.csv`;

            console.log('Generating CSV content for filename:', filename);
            const csvContent = "\uFEFF" + [headers.map(formatCSVField), ...rows].map(e => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", filename);
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();

            // Delay revocation to ensure browser captures the download request
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                console.log('CSV download initiated and URL revoked');
            }, 100);
        } catch (error) {
            console.error('Export CSV Error:', error);
            alert('เกิดข้อผิดพลาดในการ Export CSV: ' + error.message);
        }
    };


    return (
        <div className="bg-slate-50 min-h-screen pb-10">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8">
                {/* <div className="flex flex-col md:flex-row md:items-end justify-between gap-2 mb-3 mt-4">
                    <div>
                        <h1 className="text-lg font-black text-slate-800 tracking-tight">รายงานสรุปผลการตรวจ</h1>
                        <p className="text-slate-500 text-[10px] font-bold">วิเคราะห์สถิติและข้อมูลการตรวจสอบย้อนหลังแบบเรียลไทม์</p>
                    </div>
                </div> */}
                <div className="relative md:sticky md:top-[64px] z-40 bg-slate-50/80 pt-1.5 pb-2 mb-2 -mx-2 px-2 md:-mx-4 md:px-4 transition-all">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="grid grid-cols-2 md:flex md:flex-wrap lg:flex-nowrap gap-2 sm:gap-4 items-center">
                            {/* Search */}
                            <div className="relative col-span-2 md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหาช่องจอด/ทะเบียน..."
                                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 outline-none text-sm shadow-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Date Picker */}
                            <div className="col-span-1 w-full md:w-auto">
                                <div className="relative">
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={(date) => setSelectedDate(date)}
                                        locale="th"
                                        dateFormat="d MMM yy"
                                        maxDate={new Date()}
                                        popperPlacement="bottom-start"
                                        portalId="root"
                                        wrapperClassName="w-full"
                                        customInput={
                                            <button className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left focus:outline-none group hover:border-slate-300 transition-all focus-within:border-slate-300">
                                                <div className="p-1 rounded-lg flex-shrink-0 transition-colors bg-blue-50 group-hover:bg-blue-100">
                                                    <Calendar className="w-3.5 h-3.5 text-blue-600" />
                                                </div>
                                                <div className="flex flex-col w-full min-w-0">
                                                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-0.5">วันที่</span>
                                                    <span className="text-xs font-bold text-slate-800">
                                                        {selectedDate ? (() => {
                                                            const day = selectedDate.getDate();
                                                            const month = selectedDate.toLocaleDateString('th-TH', { month: 'short' });
                                                            const year = selectedDate.getFullYear().toString().slice(-2);
                                                            return `${day} ${month} ${year}`;
                                                        })() : 'เลือกวันที่'}
                                                    </span>
                                                </div>
                                                <div className="text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0">
                                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                                </div>
                                            </button>
                                        }
                                    />
                                </div>
                            </div>

                            {/* Round */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="รอบเวลา"
                                    icon={Clock}
                                    allLabel="ทั้งหมด"
                                    value={selectedRound}
                                    onChange={setSelectedRound}
                                    options={filteredRounds.map(r => ({ label: `${(r.scheduled_time || '00:00:00').substring(0, 5)} น.`, value: r.id }))}
                                />
                            </div>

                            {/* Vehicle Type */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="ประเภทรถ"
                                    icon={Car}
                                    allLabel="ทั้งหมด"
                                    value={selectedType}
                                    onChange={setSelectedType}
                                    options={[
                                        { label: 'รถยนต์', value: 'Car' },
                                        { label: 'จักรยานยนต์', value: 'Motorcycle' }
                                    ]}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="สถานะ"
                                    icon={Tag}
                                    allLabel="ทั้งหมด"
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    options={[
                                        { label: 'มีสติ๊กเกอร์', value: 'Normal' },
                                        { label: 'สติ๊กเกอร์ไม่ชัดเจน', value: 'Unclear' },
                                        { label: 'ไม่มีรถจอด', value: 'Empty' },
                                        { label: 'ไม่มีสติ๊กเกอร์', value: 'No-Sticker' },
                                        { label: 'Visitor', value: 'Visitor' }
                                    ]}
                                />
                            </div>

                            {/* Export CSV - Shifting to right on desktop */}
                            <div className="col-span-2 md:w-auto md:ml-auto">
                                <button
                                    onClick={exportToCSV}
                                    className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-emerald-500/10 transition-all active:scale-95 border border-emerald-500/20"
                                >
                                    <FileText className="w-3 h-3 shrink-0" />
                                    <span className="truncate">Export CSV</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div ref={reportRef} className="p-1 -m-1">
                    {/* Summary Section */}
                    <div className="mb-6">
                        {/* Top KPI Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {/* Total Audited */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ตรวจสอบแล้ว</p>
                                        <div className="flex items-baseline gap-1 mt-0.5">
                                            <span className="text-xl font-black text-slate-800">{(summary.unique_slots_audited || 0).toLocaleString()}</span>
                                            <span className="text-xs font-bold text-slate-400">/ {(summary.total_slots || 0).toLocaleString()}</span>
                                        </div>
                                        <p className="text-[10px] font-medium text-slate-400">รายการ (จากช่องจอดทั้งหมด)</p>
                                    </div>
                                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 ml-2">
                                        <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        </svg>
                                    </div>
                                </div>
                                {summary.total_slots > 0 && (
                                    <div>
                                        <div className="flex justify-between items-center mb-0.5">
                                            <span className="text-[10px] font-bold text-slate-400">ความครอบคลุม</span>
                                            <span className="text-[10px] font-black text-blue-600">{Math.round((summary.unique_slots_audited / summary.total_slots) * 100)}%</span>
                                        </div>
                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${Math.min(100, Math.round((summary.unique_slots_audited / summary.total_slots) * 100))}%` }} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Compliance Rate */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">อัตราความถูกต้อง</p>
                                        <div className="flex items-baseline gap-0.5 mt-0.5">
                                            <span className={`text-xl font-black ${successRate >= 80 ? 'text-emerald-600' : successRate >= 60 ? 'text-amber-600' : 'text-rose-600'}`}>{successRate}</span>
                                            <span className={`text-xs font-bold ${successRate >= 80 ? 'text-emerald-400' : successRate >= 60 ? 'text-amber-400' : 'text-rose-400'}`}>%</span>
                                        </div>
                                        <p className="text-[10px] font-medium text-slate-400">{successRate >= 80 ? 'อยู่ในเกณฑ์ดี' : successRate >= 60 ? 'ต้องติดตาม' : 'ต้องแก้ไขด่วน'}</p>
                                    </div>
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ml-2 ${successRate >= 80 ? 'bg-emerald-50' : successRate >= 60 ? 'bg-amber-50' : 'bg-rose-50'}`}>
                                        <svg className={`w-4 h-4 ${successRate >= 80 ? 'text-emerald-500' : successRate >= 60 ? 'text-amber-500' : 'text-rose-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-0.5">
                                        <span className="text-[10px] font-bold text-slate-400">จากบันทึกทั้งหมด {counts.Total} รายการ</span>
                                    </div>
                                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full transition-all duration-700 ${successRate >= 80 ? 'bg-emerald-500' : successRate >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${successRate}%` }} />
                                    </div>
                                </div>
                            </div>

                            {/* Issues */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">พบปัญหา</p>
                                        <div className="flex items-baseline gap-1 mt-0.5">
                                            <span className="text-xl font-black text-rose-600">
                                                {Number(counts['No-Sticker'] || 0) + Number(counts.Unclear || 0) + Number(summary.overstay_count || 0) + Number(summary.handbrake_count || 0)}
                                            </span>
                                            <span className="text-xs font-bold text-slate-400">รายการ</span>
                                        </div>
                                        <p className="text-[10px] font-medium text-slate-400 truncate">สติ๊กเกอร์ผิดระเบียบ + รถจอดแช่ + ไม่ปลดเบรกมือ</p>
                                    </div>
                                    <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center shrink-0 ml-2">
                                        <svg className="w-4 h-4 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-1 mt-2">
                                    <div className="bg-rose-50 rounded-lg p-1 text-center border border-rose-100/50">
                                        <div className="text-sm font-black text-rose-600">{counts['No-Sticker'] || 0}</div>
                                        <div className="text-[8px] font-bold text-rose-400 uppercase tracking-tight truncate" title="ไม่มีสติ๊กเกอร์">ไม่มีสติ๊กเกอร์</div>
                                    </div>
                                    <div className="bg-amber-50 rounded-lg p-1 text-center border border-amber-100/50">
                                        <div className="text-sm font-black text-amber-600">{counts.Unclear || 0}</div>
                                        <div className="text-[8px] font-bold text-amber-400 uppercase tracking-tight truncate" title="ไม่ชัดเจน">ไม่ชัดเจน</div>
                                    </div>
                                    <div className="bg-orange-50 rounded-lg p-1 text-center border border-orange-100/50">
                                        <div className="text-sm font-black text-orange-600">{summary.overstay_count || 0}</div>
                                        <div className="text-[8px] font-bold text-orange-400 uppercase tracking-tight truncate" title="รถจอดแช่">รถจอดแช่</div>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-1 text-center border border-red-100/50">
                                        <div className="text-sm font-black text-red-600">{summary.handbrake_count || 0}</div>
                                        <div className="text-[8px] font-bold text-red-400 uppercase tracking-tight truncate" title="ไม่ปลดเบรกมือ">ไม่ปลดเบรกมือ</div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Inspector Info */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ข้อมูลผู้ตรวจ</p>
                                        <p className="text-sm font-black text-slate-800 mt-1 truncate" title={summary.inspector_metrics?.inspector_names || '-'}>
                                            {summary.inspector_metrics?.inspector_names || '-'}
                                        </p>
                                        <p className="text-[10px] font-medium text-slate-400">ชื่อผู้ตรวจ</p>
                                    </div>
                                    <div className="w-8 h-8 bg-slate-50 rounded-lg flex items-center justify-center shrink-0 ml-2 border border-slate-100">
                                        <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 border-t border-slate-50 pt-2">
                                    <div>
                                        <div className="flex items-center gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                                            <span className="text-[10px] font-black text-slate-500">เริ่ม:</span>
                                            <span className="text-[10px] font-bold text-slate-700">
                                                {summary.inspector_metrics?.start_at ? new Date(summary.inspector_metrics.start_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.' : '-'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 mt-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-rose-400"></div>
                                            <span className="text-[10px] font-black text-slate-500">จบ:</span>
                                            <span className="text-[10px] font-bold text-slate-700">
                                                {summary.inspector_metrics?.end_at ? new Date(summary.inspector_metrics.end_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) + ' น.' : '-'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg flex flex-col items-center justify-center border border-slate-100">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight">ระยะเวลา</span>
                                        <div className="text-[11px] font-black text-indigo-600 leading-none mt-0.5">
                                            {(() => {
                                                const mins = summary.inspector_metrics?.duration_minutes;
                                                if (!mins && mins !== 0) return '-';
                                                if (mins < 60) return `${mins} น.`;
                                                return `${Math.floor(mins / 60)} ชม. ${mins % 60} น.`;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Statistics Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

                        {/* ── Pie Chart: Status Distribution ── */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col">
                            <div className="flex items-center gap-2 mb-5">
                                <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800">สัดส่วนสถานะการจอดรถ</h3>
                                    <p className="text-[10px] font-medium text-slate-400">รวม {counts.Total} รายการที่บันทึก</p>
                                </div>
                            </div>

                            {pieData.length > 0 ? (
                                <div className="flex flex-row gap-4 sm:gap-6 items-center flex-1">
                                    {/* Donut Chart */}
                                    <div className="w-44 h-44 shrink-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={52}
                                                    outerRadius={72}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                    stroke="none"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                    <Label
                                                        content={({ viewBox }) => {
                                                            const { cx, cy } = viewBox;
                                                            return (
                                                                <g>
                                                                    <text x={cx} y={cy - 6} textAnchor="middle" className="font-black" style={{ fontSize: '20px', fill: '#1e293b', fontWeight: 900 }}>
                                                                        {summary.total_slots || 0}
                                                                    </text>
                                                                    <text x={cx} y={cy + 12} textAnchor="middle" style={{ fontSize: '10px', fill: '#94a3b8', fontWeight: 700 }}>
                                                                        ช่องจอด
                                                                    </text>
                                                                </g>
                                                            );
                                                        }}
                                                    />
                                                </Pie>
                                                <RechartsTooltip
                                                    formatter={(value, name) => [`${value} รายการ`, name]}
                                                    contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 8px 24px rgb(0 0 0 / 0.12)', fontSize: '12px', fontWeight: 'bold' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Legend Cards */}
                                    <div className="flex-1 space-y-2 min-w-0">
                                        {pieData.map((entry) => {
                                            const totalForPct = summary.total_slots || 1;
                                            const pct = Math.round((entry.value / totalForPct) * 100);
                                            return (
                                                <div key={entry.name} className="flex items-center gap-2">
                                                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-center mb-0.5">
                                                            <span className="text-[11px] font-bold text-slate-600 truncate">{entry.name}</span>
                                                            <span className="text-[11px] font-black text-slate-800 ml-2 shrink-0">{entry.value}</span>
                                                        </div>
                                                        <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: entry.color }} />
                                                        </div>
                                                    </div>
                                                    <span className="text-[10px] font-bold text-slate-400 w-7 text-right shrink-0">{pct}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-slate-300 text-sm font-bold">ไม่มีข้อมูล</div>
                            )}
                        </div>

                        {/* ── Zone Issues: Horizontal Bar ── */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-rose-50 rounded-lg flex items-center justify-center">
                                        <svg className="w-4 h-4 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-black text-slate-800">ปัญหาแบ่งตามโซน</h3>
                                        <p className="text-[10px] font-medium text-slate-400">สติ๊กเกอร์ผิดระเบียบ + รถจอดแช่ + ไม่ปลดเบรกมือ</p>
                                    </div>
                                </div>
                                {/* Legend pills */}
                                <div className="sm:ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-rose-500">
                                        <span className="w-2 h-2 rounded-sm bg-rose-400 inline-block"></span>ไม่มีสติ๊กเกอร์
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-amber-500">
                                        <span className="w-2 h-2 rounded-sm bg-amber-400 inline-block"></span>ไม่ชัดเจน
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-orange-500">
                                        <span className="w-2 h-2 rounded-sm bg-orange-400 inline-block"></span>รถจอดแช่
                                    </span>
                                    <span className="flex items-center gap-1 text-[10px] font-bold text-red-500">
                                        <span className="w-2 h-2 rounded-sm bg-red-500 inline-block"></span>ไม่ปลดเบรกมือ
                                    </span>
                                </div>
                            </div>

                            {barData.length > 0 ? (
                                <div className="flex-1 space-y-3 overflow-y-auto max-h-56">
                                    {(() => {
                                        const maxTotal = Math.max(...barData.map(d => d.totalIssues), 1);
                                        return barData.map((zone) => (
                                            <div key={zone.name}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-[11px] font-black text-slate-700">{zone.name}</span>
                                                    <span className="text-[11px] font-black text-slate-500">{zone.totalIssues} รายการ</span>
                                                </div>
                                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                                                    {zone['ไม่มีสติ๊กเกอร์'] > 0 && (
                                                        <div
                                                            className="h-full bg-rose-400 flex items-center justify-center transition-all duration-700"
                                                            style={{ width: `${(zone['ไม่มีสติ๊กเกอร์'] / maxTotal) * 100}%` }}
                                                            title={`ไม่มีสติ๊กเกอร์: ${zone['ไม่มีสติ๊กเกอร์']}`}
                                                        >
                                                            <span className="text-[8px] font-black text-white px-1">{zone['ไม่มีสติ๊กเกอร์']}</span>
                                                        </div>
                                                    )}
                                                    {zone['ไม่ชัดเจน'] > 0 && (
                                                        <div
                                                            className="h-full bg-amber-400 flex items-center justify-center transition-all duration-700"
                                                            style={{ width: `${(zone['ไม่ชัดเจน'] / maxTotal) * 100}%` }}
                                                            title={`ไม่ชัดเจน: ${zone['ไม่ชัดเจน']}`}
                                                        >
                                                            <span className="text-[8px] font-black text-white px-1">{zone['ไม่ชัดเจน']}</span>
                                                        </div>
                                                    )}
                                                    {zone['รถจอดแช่'] > 0 && (
                                                        <div
                                                            className="h-full bg-orange-400 flex items-center justify-center transition-all duration-700 border-l border-orange-500/20"
                                                            style={{ width: `${(zone['รถจอดแช่'] / maxTotal) * 100}%` }}
                                                            title={`รถจอดแช่: ${zone['รถจอดแช่']}`}
                                                        >
                                                            <span className="text-[8px] font-black text-white px-1">{zone['รถจอดแช่']}</span>
                                                        </div>
                                                    )}
                                                    {zone['ไม่ปลดเบรกมือ'] > 0 && (
                                                        <div
                                                            className="h-full bg-red-500 flex items-center justify-center transition-all duration-700 border-l border-red-600/20"
                                                            style={{ width: `${(zone['ไม่ปลดเบรกมือ'] / maxTotal) * 100}%` }}
                                                            title={`ไม่ปลดเบรกมือ: ${zone['ไม่ปลดเบรกมือ']}`}
                                                        >
                                                            <span className="text-[8px] font-black text-white px-1">{zone['ไม่ปลดเบรกมือ']}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-emerald-500">
                                    <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center">
                                        <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-sm font-black text-emerald-600">ทุกโซนปกติ</div>
                                        <div className="text-[10px] font-medium text-emerald-400 mt-0.5">ไม่พบปัญหาการจอดรถในช่วงเวลานี้</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Consecutive Parking Awareness Section */}
                    {consecutiveLogs.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border-2 border-orange-100 p-5 mb-6 overflow-hidden relative">
                            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                                <svg className="w-24 h-24 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>

                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                                    <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800">ระบบเฝ้าระวัง: รถจอดแช่ (Overstay 15 วันขึ้นไป)</h3>
                                    <p className="text-[10px] font-medium text-slate-400">พบ {consecutiveLogs.length} รายการที่จอดช่องเดิมติดต่อกันนานผิดปกติ</p>
                                </div>
                            </div>

                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left border-separate border-spacing-y-2">
                                    <thead>
                                        <tr className="text-slate-400 text-[10px] font-black tracking-widest">
                                            <th className="px-4 py-2">ช่องจอด</th>
                                            <th className="px-4 py-2 text-center">ประเภท</th>
                                            <th className="px-4 py-2 text-center">Sticker No.</th>
                                            <th className="px-4 py-2">ทะเบียนรถ</th>
                                            <th className="px-4 py-2">วันที่เริ่ม</th>
                                            <th className="px-4 py-2">ล่าสุดเมื่อ</th>
                                            <th className="px-4 py-2 text-center">จำนวนวัน</th>
                                            <th className="px-4 py-2">หมายเหตุ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {consecutiveLogs.map((log, idx) => {
                                            const days = log.consecutive_days;
                                            const isHighAlert = days >= 30;

                                            const formatDate = (dateStr) => {
                                                const d = new Date(dateStr);
                                                const day = d.getDate().toString().padStart(2, '0');
                                                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                                const year = d.getFullYear();
                                                return `${day}/${month}/${year}`;
                                            };

                                            return (
                                                <tr
                                                    key={idx}
                                                    onClick={() => fetchVehicleHistory(log.license_plate)}
                                                    className="bg-slate-50/50 hover:bg-slate-100/80 transition-all cursor-pointer group border-y border-transparent hover:border-slate-200"
                                                >
                                                    <td className="px-4 py-3 rounded-l-xl">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md group-hover:bg-blue-500 group-hover:text-white transition-colors shadow-sm">
                                                                {log.slot_name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center justify-center">
                                                            {log.vehicle_type ? (
                                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${log.vehicle_type === 'Motorcycle' ? 'bg-purple-50 text-purple-600 border border-purple-100' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                                                                    {log.vehicle_type === 'Motorcycle' ? 'จยย.' : 'รถยนต์'}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-300 font-medium italic">-</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.sticker_no || <span className="font-black text-slate-700 font-medium italic">-</span>}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.license_plate}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {formatDate(log.start_date)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {formatDate(log.end_date)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`text-[10px] font-black ${isHighAlert ? 'text-red-500' : 'text-orange-500'}`}>{days} วัน</span>
                                                    </td>
                                                    <td className="px-4 py-3 rounded-r-xl">
                                                        <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase ${isHighAlert
                                                            ? 'bg-red-100 text-red-600 border border-red-200'
                                                            : 'bg-orange-100 text-orange-600 border border-orange-200'
                                                            }`}>
                                                            {isHighAlert ? 'รถจอดเกิน 30 วัน' : 'รถจอดเกิน 15 วัน'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card List */}
                            <div className="md:hidden space-y-3">
                                {consecutiveLogs.map((log, idx) => {
                                    const days = log.consecutive_days;
                                    const isHighAlert = days >= 30;

                                    const formatDate = (dateStr) => {
                                        const d = new Date(dateStr);
                                        const day = d.getDate().toString().padStart(2, '0');
                                        const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                        const year = d.getFullYear();
                                        return `${day}/${month}/${year}`;
                                    };

                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => fetchVehicleHistory(log.license_plate)}
                                            className="bg-slate-50/50 hover:bg-slate-100/80 border border-slate-100 rounded-2xl p-4 transition-all cursor-pointer relative overflow-hidden shadow-sm"
                                        >
                                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isHighAlert ? 'bg-red-500' : 'bg-orange-500'}`} />

                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-black text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg shadow-sm">
                                                        {log.slot_name}
                                                    </span>
                                                    {log.vehicle_type && (
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${log.vehicle_type === 'Motorcycle'
                                                                ? 'bg-purple-50 text-purple-600 border-purple-100'
                                                                : 'bg-slate-100 text-slate-600 border-slate-200'
                                                            }`}>
                                                            {log.vehicle_type === 'Motorcycle' ? 'จยย.' : 'รถยนต์'}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${isHighAlert
                                                        ? 'bg-red-100 text-red-600 border-red-200'
                                                        : 'bg-orange-100 text-orange-600 border-orange-200'
                                                    }`}>
                                                    {isHighAlert ? 'รถจอดเกิน 30 วัน' : 'รถจอดเกิน 15 วัน'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-xs">
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 block mb-0.5">ทะเบียนรถ</span>
                                                    <span className="text-xs font-black text-slate-700">
                                                        {log.license_plate || '-'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-bold text-slate-400 block mb-0.5">Sticker No.</span>
                                                    <span className="text-xs font-black text-slate-700">
                                                        {log.sticker_no || '-'}
                                                    </span>
                                                </div>
                                                <div className="col-span-2 border-t border-slate-100/80 pt-2 flex justify-between items-center">
                                                    <div>
                                                        <span className="text-[10px] font-bold text-slate-400 block mb-0.5">ระยะเวลาจอด</span>
                                                        <span className="text-[10px] font-bold text-slate-600">
                                                            {formatDate(log.start_date)} - {formatDate(log.end_date)}
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-[10px] font-bold text-slate-400 block mb-0.5">จำนวนวัน</span>
                                                        <span className={`text-sm font-black ${isHighAlert ? 'text-red-500' : 'text-orange-500'}`}>
                                                            {days} วัน
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}


                    {/* Table Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left table-fixed min-w-[1200px]">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-100">
                                        <th onClick={() => requestSort('log_date')} className="w-[85px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                วันที่ {getSortIcon('log_date')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('round_time')} className="w-[65px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                รอบ {getSortIcon('round_time')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('slot_name')} className="w-[75px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                ช่องจอด {getSortIcon('slot_name')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('vehicle_type')} className="w-[65px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                ประเภทรถ {getSortIcon('vehicle_type')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('sticker_no')} className="w-[85px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1 text-[10px]">
                                                Sticker No. {getSortIcon('sticker_no')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('license_plate')} className="w-[95px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                ทะเบียนรถ {getSortIcon('license_plate')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('status')} className="w-[95px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                สถานะ {getSortIcon('status')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('is_handbrake')} className="w-[95px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                ปลดเบรกมือ {getSortIcon('is_handbrake')}
                                            </div>
                                        </th>
                                        <th className="w-[140px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-left">หมายเหตุ</th>
                                        <th onClick={() => requestSort('checker_name')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                ผู้ตรวจ {getSortIcon('checker_name')}
                                            </div>
                                        </th>
                                        <th onClick={() => requestSort('created_at')} className="w-[140px] px-1 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">
                                                เวลาตรวจ {getSortIcon('created_at')}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="11" className="px-4 py-20 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Loading Data</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : logs.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="px-4 py-20 text-center">
                                                <div className="flex flex-col items-center gap-2 opacity-40">
                                                    <svg className="w-12 h-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                    <span className="text-sm font-bold text-slate-500">ไม่พบข้อมูลในระยะเวลาที่กำหนด</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        currentLogs.map((log) => (
                                            <tr key={log.id} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {(() => {
                                                                const d = new Date(log.log_date);
                                                                const day = d.getDate().toString().padStart(2, '0');
                                                                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                                                const year = d.getFullYear();
                                                                return `${day}/${month}/${year}`;
                                                            })()}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.round_time?.substring(0, 5)} น.
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none tracking-tighter">
                                                            {log.slot_name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        {log.status === 'Empty' ? (
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">-</span>
                                                        ) : (
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                                {log.vehicle_type === 'Motorcycle' ? 'จยย.' : 'รถยนต์'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.sticker_no || <span className="text-slate-300 font-medium italic">-</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.license_plate ? log.license_plate : <span className="text-slate-300 font-medium italic">-</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold tracking-wider leading-none">
                                                            {log.status === 'Normal' ? 'มีสติ๊กเกอร์' :
                                                             log.status === 'Unclear' ? 'สติ๊กเกอร์ไม่ชัดเจน' :
                                                             log.status === 'Empty' ? 'ไม่มีรถจอด' :
                                                             log.status === 'No-Sticker' ? 'ไม่มีสติ๊กเกอร์' :
                                                             log.status === 'Visitor' ? 'Visitor' : log.status}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        {log.is_handbrake === 1 || log.is_handbrake === '1' ? (
                                                            <span className="text-[10px] font-black text-slate-600 font-bold tracking-wider leading-none">
                                                                ปลดแล้ว
                                                            </span>
                                                        ) : log.is_handbrake === 0 || log.is_handbrake === '0' ? (
                                                            <span className="text-[10px] font-black text-slate-600 font-bold tracking-wider leading-none">
                                                                ยังไม่ปลด
                                                            </span>
                                                        ) : (
                                                            <span className="text-slate-300 font-medium italic">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle">
                                                    <div className="flex items-center max-w-[130px]">
                                                        <span className="text-[10px] text-slate-600 font-bold max-w-[120px] truncate" title={log.remark}>
                                                            {log.remark || <span className="text-slate-300 font-medium italic">-</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {log.checker_name || <span className="text-slate-400 font-bold italic">System</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                            {new Date(log.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(',', '')}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {logs.length > 0 && (
                            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                            className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50 outline-none"
                                        >
                                            <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                        </button>

                                        {isRowsDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                                <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                    <div className="py-0.5">
                                                        {[10, 20, 50, 100].map(size => (
                                                            <button
                                                                key={size}
                                                                onClick={() => {
                                                                    setRowsPerPage(size);
                                                                    setIsRowsDropdownOpen(false);
                                                                    setCurrentPage(1);
                                                                }}
                                                                className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                            >
                                                                <span>{size}</span>
                                                                {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                        {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                        className={`p-1.5 rounded-lg transition-all ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="First Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className={`p-1.5 rounded-lg transition-all ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Previous Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                    </button>

                                    <div className="flex items-center gap-1 px-2">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black transition-all ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className={`p-1.5 rounded-lg transition-all ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Next Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className={`p-1.5 rounded-lg transition-all ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Last Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Vehicle History Modal */}
            {isHistoryModalOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/70" onClick={() => setIsHistoryModalOpen(false)}></div>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative animate-in fade-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-800">ประวัติการตรวจสอบรายการจอด</h3>
                                    <p className="text-xs font-bold text-blue-600">ทะเบียน: {selectedPlate}</p>
                                </div>
                            </div>
                            <button onClick={() => setIsHistoryModalOpen(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingHistory ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                                    <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mb-2"></div>
                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest animate-pulse">Loading History</span>
                                </div>
                            ) : plateHistory.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-slate-100">
                                                <th className="px-2 py-1.5 text-center">ลำดับ</th>
                                                <th className="px-2 py-1.5 text-center">วันที่</th>
                                                <th className="px-2 py-1.5 text-center">รอบ</th>
                                                <th className="px-2 py-1.5 text-center">ช่องจอด</th>
                                                <th className="px-2 py-1.5 text-center">สถานะ</th>
                                                <th className="px-2 py-1.5">หมายเหตุ</th>
                                                <th className="px-2 py-1.5 text-center">ผู้ตรวจ</th>
                                                <th className="px-2 py-1.5 text-center">เวลาตรวจ</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {historyWithDayNumbers.map((item, idx) => (
                                                <tr key={idx} className={`hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0`}>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap min-w-[70px]">
                                                        <div className="flex items-center justify-center h-6">
                                                            {item.isFirstInDay ? (
                                                                <span className={`inline-flex items-center justify-center text-[10px] font-black px-2 py-0.5 rounded-lg border shadow-sm h-5 leading-none ${item.dayNumber >= 15 ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                                                    วันที่ {item.dayNumber}
                                                                </span>
                                                            ) : (
                                                                <div className="border-l-2 border-slate-200 h-4"></div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap min-w-[80px]">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                                {item.isFirstInDay ? (() => {
                                                                    const d = new Date(item.log_date);
                                                                    const day = d.getDate().toString().padStart(2, '0');
                                                                    const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                                                    const year = d.getFullYear();
                                                                    return `${day}/${month}/${year}`;
                                                                })() : ""}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                                {item.round_time?.substring(0, 5)} น.
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className="inline-flex items-center justify-center text-[10px] font-black text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 tracking-tighter h-5 leading-none">
                                                                {item.slot_name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className={`inline-flex items-center justify-center text-[10px] font-black px-1.5 py-0.5 rounded-full h-5 leading-none ${item.status === 'Normal' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                                                item.status === 'No-Sticker' ? 'bg-rose-50 text-rose-600 border border-rose-100' :
                                                                    item.status === 'Unclear' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                                                        'bg-slate-50 text-slate-600 border border-slate-100'
                                                                }`}>
                                                                {item.status}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle">
                                                        <div className="flex items-center h-6 max-w-[120px]">
                                                            <span className="text-[10px] font-bold text-slate-500 truncate leading-none" title={item.remark}>
                                                                {item.remark || '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                                {item.checker_name || '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-2 align-middle whitespace-nowrap">
                                                        <div className="flex items-center justify-center h-6 text-center">
                                                            <span className="text-[10px] font-black text-slate-600 font-bold leading-none">
                                                                {item.created_at ? (() => {
                                                                    const d = new Date(item.created_at);
                                                                    const day = d.getDate().toString().padStart(2, '0');
                                                                    const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                                                    const year = d.getFullYear();
                                                                    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                                                                    return `${day}/${month}/${year} ${time}`;
                                                                })() : '-'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-20 text-slate-300 font-bold">ไม่พบประวัติการตรวจสอบ</div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
                            <button
                                onClick={() => setIsHistoryModalOpen(false)}
                                className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-black text-xs transition-all active:scale-95"
                            >
                                ยกเลิก
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportManagement;
